import React from 'react';
import PropTypes from 'prop-types';
import { List } from './list';
import { openCookiePreferencesModal } from '../index';
import { TRUE, FALSE, MENUITEM, MENU, NONE } from '../../modules/constants';
import { track as tracking } from '../../modules/tracking';

/**
 * Describes a link with an optional sub navigation collection of children
 *
 * @function
 * @param       {String} href              Should contain a link to the href target of the initial anchor. This is omitted for items which have a sub menu as tracking was interfering with the event cancellation leading to the link always being followed
 * @param       {string} text              The text of the link.
 * @param       {string} title             A slightly longer descriptive text for the title attribute of the link.
 * @param       {array}  children          An optional collection of menu items
 * @param       {boolean} inList           If we are already in a list, this will be true and force the omission of the opening ul tag.
 * @param       {string} className         An optional class to add to the existing component class attribute.
 * @param       {string} openMenu          useState variable, which will contain the name of the open sub menu if this applies.
 * @param       {function} setOpenMenu     useState function to set the name of the open sub menu on click.
 * @param       {boolean} openMobileMenu   Is true if the menu opener button clicked was the burger menu button for mobile.
 * @param       {function} setOpenMobileMenu useState function to set if the menu is opened using the burger menu button for mobile
 * @constructor
 */
function Link({
    action,
    href,
    target,
    text,
    title,
    children,
    inList,
    track,
    className,
    openMenu,
    setOpenMenu,
    openMobileMenu,
    setOpenMobileMenu
}) {
    function Anchor() {
        const listProps = {
            openMenu,
            setOpenMenu,
            openMobileMenu,
            setOpenMobileMenu,
            text
        };

        function openMenuFn() {
            setOpenMenu(openMenu !== text ? (text ? text : '') : '');
        }

        function trackingFn(open) {
            tracking(`${track}: Click: ${open ? 'Open' : 'Close'}: ${text}`)();
        }

        function rmClass(e) {
            e.target.classList.remove('open');
            e.target.nextSibling.classList.remove('open');
        }

        return (
            <>
                {children && children.length ? (
                    <>
                        <a
                            href={href || '#'}
                            title={title}
                            className={[
                                text === openMenu ? 'open' : null,
                                className
                            ]
                                .join(' ')
                                .trim()}
                            aria-haspopup={TRUE}
                            role={MENUITEM}
                            target={target}
                            aria-expanded={openMenu ? TRUE : FALSE}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (openMenu === text) {
                                    setTimeout(openMenuFn, 100);
                                    rmClass(e);
                                } else {
                                    openMenuFn();
                                }
                                trackingFn(openMenu !== text);
                            }}
                        >
                            {text}
                        </a>
                        <List role={MENU} {...listProps}>
                            {children}
                        </List>
                    </>
                ) : (
                    <a
                        role={MENUITEM}
                        href={href}
                        target={target}
                        title={title}
                        className={className}
                        onClick={(e) => {
                            trackingFn(openMenu !== text);
                            if (action === 'open-cookies') {
                                openCookiePreferencesModal(e);
                            }
                        }}
                    >
                        {text}
                    </a>
                )}
            </>
        );
    }

    if (inList) {
        return (
            <li
                className={[text === openMenu ? 'open' : null, className]
                    .join(' ')
                    .trim()}
                role={NONE}
            >
                <Anchor />
            </li>
        );
    }

    return <Anchor />;
}

Link.propTypes = {
    action: PropTypes.string,
    href: PropTypes.string,
    target: PropTypes.string,
    text: PropTypes.string,
    title: PropTypes.string,
    track: PropTypes.string,
    children: PropTypes.array,
    inList: PropTypes.bool,
    className: PropTypes.string,
    openMenu: PropTypes.string,
    setOpenMenu: PropTypes.func,
    openMobileMenu: PropTypes.bool,
    setOpenMobileMenu: PropTypes.func
};

export { Link };
